import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Menu, X } from "lucide-react";
import LanguageCurrencySelector from "../components/LanguageCurrencySelector";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const AboutPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const logoClickTimeout = useRef(null);

  const handleLogoClick = () => {
    setLogoClickCount(prev => prev + 1);
    if (logoClickTimeout.current) clearTimeout(logoClickTimeout.current);
    logoClickTimeout.current = setTimeout(() => setLogoClickCount(0), 2000);
    if (logoClickCount + 1 >= 5) {
      navigate('/admin');
      setLogoClickCount(0);
    }
  };

  const guidancePoints = [
    {
      title: t('about.guidance1Title'),
      description: t('about.guidance1Desc'),
      questions: [t('about.guidance1Q1'), t('about.guidance1Q2'), t('about.guidance1Q3')]
    },
    {
      title: t('about.guidance2Title'),
      description: t('about.guidance2Desc'),
      questions: [t('about.guidance2Q1'), t('about.guidance2Q2'), t('about.guidance2Q3')]
    },
    {
      title: t('about.guidance3Title'),
      description: t('about.guidance3Desc'),
      questions: [t('about.guidance3Q1'), t('about.guidance3Q2'), t('about.guidance3Q3')]
    },
    {
      title: t('about.guidance4Title'),
      description: t('about.guidance4Desc'),
      questions: [t('about.guidance4Q1'), t('about.guidance4Q2'), t('about.guidance4Q3')]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-amber-50/30">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-rose-100/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-36">
            <button onClick={handleLogoClick} className="flex items-center">
              <img src={LOGO_URL} alt="SingleSeen" className="h-36 w-auto" />
            </button>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-rose-600 hover:text-rose-800">{t('nav.home')}</Link>
              <Link to="/about" className="text-rose-700 font-medium">{t('nav.about')}</Link>
              <Link to="/pricing" className="text-rose-600 hover:text-rose-800">{t('nav.pricing')}</Link>
              <Link to="/contact" className="text-rose-600 hover:text-rose-800">{t('nav.contact')}</Link>
            </div>

            <div className="flex items-center gap-4">
              {user ? (
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full px-6"
                >
                  {t('nav.dashboard')}
                </Button>
              ) : (
                <>
                  <Link to="/login" className="text-rose-600 hover:text-rose-800 font-medium hidden sm:block">{t('nav.login')}</Link>
                  <Button 
                    onClick={() => navigate("/register")}
                    className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full px-6"
                  >
                    {t('nav.getStarted')}
                  </Button>
                </>
              )}
              <button 
                className="md:hidden p-2 text-rose-700"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-rose-100 py-4 px-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <Link to="/" className="text-rose-700 py-2">{t('nav.home')}</Link>
              <Link to="/about" className="text-rose-700 py-2 font-medium">{t('nav.about')}</Link>
              <Link to="/pricing" className="text-rose-700 py-2">{t('nav.pricing')}</Link>
              <Link to="/contact" className="text-rose-700 py-2">{t('nav.contact')}</Link>
              <div className="py-2">
                <LanguageCurrencySelector />
              </div>
            </div>
          </div>
        )}
      </nav>

      <main>
        {/* Hero Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 pt-40">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-rose-900 mb-6 leading-tight">
              {t('about.heroTitle')}<br />
              <span className="gradient-text">SingleSeen</span>
            </h1>
            <p className="text-xl text-rose-600 max-w-2xl mx-auto leading-relaxed">
              {t('about.heroSubtitle')}
            </p>
          </div>
        </section>

        {/* The Name Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-32 h-32 mx-auto md:mx-0 mb-6 bg-gradient-to-br from-rose-100 to-pink-200 rounded-3xl flex items-center justify-center shadow-lg shadow-rose-200/50">
                  <span className="text-7xl font-serif text-rose-700" style={{ fontFamily: "'Amiri', serif" }}>س</span>
                </div>
              </div>
              <div>
                <h2 className="font-serif text-3xl text-rose-900 mb-4">{t('about.whySeenTitle')}</h2>
                <p className="text-rose-600/80 leading-relaxed mb-4">
                  {t('about.whySeenP1')}
                </p>
                <p className="text-rose-600/80 leading-relaxed mb-4">
                  {t('about.whySeenP2')}
                </p>
                <p className="text-rose-600/80 leading-relaxed">
                  {t('about.whySeenP3')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Vision Section */}
        <section className="py-16 bg-gradient-to-b from-rose-50/50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl text-rose-900 mb-4">{t('about.visionTitle')}</h2>
              <p className="text-rose-600 max-w-2xl mx-auto">
                {t('about.visionSubtitle')}
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-rose-100">
              <div className="mb-6 border-l-4 border-rose-300 pl-6">
                <p className="text-lg text-rose-700 italic leading-relaxed">
                  "{t('about.visionQuote')}"
                </p>
              </div>
              
              <p className="text-rose-600/80 leading-relaxed mb-6">
                {t('about.visionP1')}
              </p>

              <p className="text-rose-600/80 leading-relaxed mb-6">
                {t('about.visionP2')}
              </p>

              <p className="text-rose-700 font-medium">
                {t('about.visionP3')}
              </p>
            </div>
          </div>
        </section>

        {/* Guidance Section */}
        <section className="py-20 bg-white" id="guidance">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-rose-500 font-medium tracking-wide uppercase text-sm mb-4 block">
                {t('about.guidanceFromSunnah')}
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl text-rose-900 mb-4">
                {t('about.guidanceTitle')}
              </h2>
              <p className="text-rose-600 max-w-2xl mx-auto">
                {t('about.guidanceSubtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {guidancePoints.map((point, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-8 border border-rose-100 hover:border-rose-200 transition-colors"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <span className="text-3xl font-light text-rose-300">0{index + 1}</span>
                    <h3 className="font-serif text-xl text-rose-900 pt-1">{point.title}</h3>
                  </div>
                  <p className="text-rose-600/80 leading-relaxed mb-4">{point.description}</p>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-rose-700">{t('about.askYourself')}</p>
                    {point.questions.map((q, qIdx) => (
                      <div key={qIdx} className="flex items-start gap-2">
                        <span className="text-rose-400 mt-0.5">→</span>
                        <span className="text-rose-600/80 text-sm">{q}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What NOT to Prioritize Section */}
        <section className="py-16 bg-gradient-to-b from-rose-50/30 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl text-rose-900 mb-4">{t('about.cautionTitle')}</h2>
              <p className="text-rose-600 max-w-2xl mx-auto">
                {t('about.cautionSubtitle')}
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-rose-100">
              <div className="grid sm:grid-cols-3 gap-6">
                <div className="text-center p-4">
                  <span className="text-4xl font-light text-rose-300 block mb-3">01</span>
                  <h4 className="font-serif text-rose-900 mb-2">{t('about.beautyAlone')}</h4>
                  <p className="text-rose-600/70 text-sm">{t('about.beautyAloneDesc')}</p>
                </div>
                <div className="text-center p-4">
                  <span className="text-4xl font-light text-rose-300 block mb-3">02</span>
                  <h4 className="font-serif text-rose-900 mb-2">{t('about.wealthAlone')}</h4>
                  <p className="text-rose-600/70 text-sm">{t('about.wealthAloneDesc')}</p>
                </div>
                <div className="text-center p-4">
                  <span className="text-4xl font-light text-rose-300 block mb-3">03</span>
                  <h4 className="font-serif text-rose-900 mb-2">{t('about.statusAlone')}</h4>
                  <p className="text-rose-600/70 text-sm">{t('about.statusAloneDesc')}</p>
                </div>
              </div>
              <p className="text-center text-rose-600 mt-6 text-sm italic">
                {t('about.hadithQuote')}
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-serif text-3xl sm:text-4xl text-white mb-4">
              {t('about.ctaTitle')}
            </h2>
            <p className="text-rose-100 text-lg mb-8 max-w-2xl mx-auto">
              {t('about.ctaSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/register")}
                className="bg-white text-rose-600 hover:bg-rose-50 rounded-full px-8 h-12 text-lg font-medium"
              >
                {t('about.createProfile')}
              </Button>
              <Button 
                onClick={() => navigate("/pricing")}
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 h-12 text-lg font-medium"
              >
                {t('common.viewPricing')}
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-rose-900 text-rose-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="SingleSeen" className="h-10 w-auto brightness-0 invert opacity-80" />
            </div>
            <div className="flex gap-8 text-sm">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">{t('nav.privacy')}</Link>
              <Link to="/terms" className="hover:text-white transition-colors">{t('nav.terms')}</Link>
              <Link to="/contact" className="hover:text-white transition-colors">{t('nav.contact')}</Link>
              <Link to="/help" className="hover:text-white transition-colors">{t('nav.help')}</Link>
            </div>
            <p className="text-sm">© 2025 SingleSeen. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
